<template>
	<Tab ref="tab" :class="{ open }" v-bind="$props" @toggle="toggle">
		<template v-slot:header>
			<TabHeader
				icon="total"
				label="Your results"
				timeframe="year"
				format="currency"
				:value="totalSavings"
			/>
		</template>
		<template v-slot:content>
			<Corridor>
				<h2 class="tab__prompt">Your containers could make:</h2>
				<!-- Desktop view -->
				<div class="tab__results">
					<RecycleResult
						v-for="output in outputs"
						:key="output.name"
						v-bind="output"
					/>
				</div>
			</Corridor>
			<!-- Mobile view -->
			<Gallery class="tab__gallery" @hook:mounted="onGalleryMount">
				<GallerySlide v-for="output in outputs" :key="output.name">
					<RecycleResult v-bind="output" />
				</GallerySlide>
			</Gallery>
		</template>
	</Tab>
</template>

<script>
import Gallery from '@/components/Gallery'
import GallerySlide from '@/components/GallerySlide'
import RecycleResult from '@/components/RecycleResult'
import tab from '@/mixins/tab'
import OutputRange from '@/lib/OutputRange'

// Container value calculation and outputs
const CONTAINER_VALUE = 10
const WEEKS = 52
const RANGES = {
	plastic: {
		icon: 'plastic',
		measure: 'plastic bottles',
		outputs: new OutputRange([
			{
				name: 'jumper',
				icon: 'plastic-jumper',
				value: 35
			},
			{
				name: 'large dog bed',
				icon: 'plastic-dog-bed',
				value: 56
			},
			{
				name: 'chair',
				icon: 'plastic-chair',
				value: 111
			},
			{
				name: 'rug',
				icon: 'plastic-rug',
				value: 2000
			}
		])
	},
	aluminium: {
		icon: 'aluminium',
		measure: 'aluminium cans',
		outputs: new OutputRange([
			{
				name: 'bicycle',
				icon: 'aluminium-bicycle',
				value: 67
			},
			{
				name: 'venetian blind',
				icon: 'aluminium-blinds',
				value: 107
			},
			{
				name: 'large pet door',
				icon: 'aluminium-pet-door',
				value: 195
			},
			{
				name: 'chair',
				icon: 'aluminium-chair',
				value: 215
			},
			{
				name: 'ceiling fan',
				icon: 'aluminium-ceiling-fan',
				value: 440
			},
			{
				name: 'dining table',
				icon: 'aluminium-dining-table',
				value: 1678
			}
		])
	},
	glass: {
		icon: 'glass',
		measure: 'glass bottles',
		outputs: new OutputRange([
			{
				name: 'car windscreen',
				icon: 'glass-windscreen',
				value: 48
			},
			{
				name: 'fish tank',
				icon: 'glass-fish-tank',
				value: 64
			},
			{
				name: 'coffee table',
				icon: 'glass-coffee-table',
				value: 70
			},
			{
				name: 'sliding door',
				icon: 'glass-sliding-door',
				value: 100
			}
		])
	}
}

export default {
	name: 'TabResults',
	props: {
		values: Array
	},
	computed: {
		totalSavings() {
			const count = this.values.reduce((acc, tab) => acc + tab.value, 0)
			return count * CONTAINER_VALUE * WEEKS
		},
		outputs() {
			return this.values.map(tab => {
				const range = RANGES[tab.name]
				const yearly = tab.value * WEEKS
				const result = range.outputs.calculate(yearly)
				return {
					name: tab.name,
					value: yearly,
					measure: range.measure,
					becomes: result.desc,
					icon: yearly > 0 ? result.icon : range.icon
				}
			})
		}
	},
	methods: {
		onGalleryMount() {
			// Re-measure tab after gallery has loaded
			this.$nextTick(this.$refs.tab.measure)
		}
	},
	mixins: [tab],
	components: {
		Gallery,
		GallerySlide,
		RecycleResult
	}
}
</script>

<style lang="scss" scoped>
@import '~scss/vars';
@import '~scss/mq';

.tab {
	background-color: $blue-65;
	background: linear-gradient(to bottom, rgba(black, 0.1), transparent 1.5rem),
		linear-gradient(to bottom, $blue-65, $blue-82 80%);

	&__prompt {
		text-align: center;
		margin: 2.5rem 0;
		color: white;
	}

	&__results {
		display: flex;
		width: 100%;
		align-items: center;
		justify-content: space-around;
		margin: 4rem 0 2rem;

		> * {
			width: 0;
			flex-grow: 1;
		}
	}

	@include mq(md) {
		&__gallery {
			display: none;
		}
	}

	@include mq-max(md) {
		&__results {
			display: none;
		}
	}
}
</style>
